import { MultiValue, SingleValue } from "react-select"
import { CardElementName } from "./CardElement"
import { TournamentFormat } from "./TournamentFormat"
import { CardVariant } from "./CardVariant"
import { ErrataSerialDictionary } from "./Errata"
import { ComboBoxOption } from "./Input"
import { TournamentRanking } from "./Tournament"
import { Archetype } from "./Archetype"

export type Deck = {
    id?: string,
    userId?: string,
    isTournamentDeck: boolean,
    playerName: string,
    rank: TournamentRanking | "",
    tournamentName: string,
    tournamentId: string | null | undefined,
    profileVariantId: string,
    displayName: string,
    isPublic: boolean,
    name: string,
    format: TournamentFormat,
    totalCardsBySerial: Record<string, number>,
    totalCardsByVariant: Record<string, number>,
    elements: CardElementName[],
    element: string,
    elementCount: Record<CardElementName, number>,
    serials: string[],
    customTags: string[] | undefined,
    notes: string | null | undefined,
    isComplete: boolean,
    createdDate?: number,
    updatedDate?: number,
    likes?: string[],
    views?: number,
    folder?: string | null,
    archetypeIds?: string[],
    archetypes?: Archetype[],
    needsArchetypeProcessing: boolean
}

export type DeleteDeckRequest = {
    id: string
}

export type DeckState = {
    nonEditableFields: {
        id: string,
        createdDate: number,
        updatedDate: number,
        userId: string,
        displayName: string,
        isTournamentDeck: boolean,
        elements: Record<CardElementName, number>,
        tournamentId: string | null | undefined,
        tournamentName?: string | null,
        views: number | undefined,
        needsArchetypeProcessing: boolean,
        archetypeIds: string[],
        archetypes: Archetype[]
    },
    editableFields: {
        saved: {
            profileVariantId: string,
            format: TournamentFormat,
            isPublic: boolean,
            name: string,
            totalCardsBySerial: Record<string, number>,
            totalCardsByVariant: Record<string, number>,
            elements: CardElementName[],
            playerName: string,
            rank: TournamentRanking | "",
            notes: string | null,
            customTags: string[] | undefined
        },
        unsaved: {
            profileVariantId: string,
            format: TournamentFormat,
            isPublic: boolean,
            name: string,
            totalCardsBySerial: Record<string, number>,
            totalCardsByVariant: Record<string, number>,
            elements: CardElementName[],
            playerName: string,
            rank: TournamentRanking | "",
            notes: string | null,
            customTags: string[] | undefined
        }
    }
    formatSelection: SingleValue<ComboBoxOption<TournamentFormat>>,
    customTagsSelections: MultiValue<ComboBoxOption<string>>,
    elementSelection: MultiValue<ComboBoxOption<CardElementName>>,
    totalCards: number,
    totalForwards: number,
    totalBackups: number,
    totalMonsters: number,
    totalSummons: number,
    totalLimitBreak: number,
    backups: Record<string, CardVariant>,
    forwards: Record<string, CardVariant>,
    monsters: Record<string, CardVariant>,
    summons: Record<string, CardVariant>,
    limitBreak: Record<string, CardVariant>,
    cards: Record<string, CardVariant>,
    totalCardsByCost: Record<number, number>,
    totalExBursts: number,
    totalSpecials: number,
    totalMultiplayable: number,
    totalCrystalGenerators: number,
    totalJobs: Record<string, number>,
    totalCategories: Record<string, number>,
    deckPrice: number,
    missingPrices: number,
    missingOctgnIds: CardVariant[],
    errata: ErrataSerialDictionary,
    bannedCards: CardVariant[],
    restrictedCards: CardVariant[],
    deckImageVariant: CardVariant | null,
    isLiked: boolean
}

export function getDefaultDeckState(id: string): DeckState {
    return {
        editableFields: {
            saved: {
                rank: "",
                profileVariantId: "",
                format: "Standard",
                isPublic: true,
                name: "",
                totalCardsBySerial: {},
                totalCardsByVariant: {},
                elements: [],
                playerName: "",
                notes: null,
                customTags: []
            },
            unsaved: {
                rank: "",
                profileVariantId: "",
                format: "Standard",
                isPublic: true,
                name: "",
                totalCardsBySerial: {},
                totalCardsByVariant: {},
                elements: [],
                playerName: "",
                notes: null,
                customTags: []
            }
        },
        nonEditableFields: {
            isTournamentDeck: false,
            displayName: "",
            createdDate: 0,
            id: id,
            updatedDate: 0,
            userId: "",
            tournamentId: "",
            tournamentName: "",
            views: 0,
            elements: { Dark: 0, Earth: 0, Fire: 0, Ice: 0, Light: 0, Lightning: 0, Water: 0, Wind: 0 },
            needsArchetypeProcessing: false,
            archetypeIds: [],
            archetypes: []
        },
        formatSelection: {
            label: "Standard",
            value: "Standard"
        },
        customTagsSelections: [],
        elementSelection: [],
        totalBackups: 0,
        totalCards: 0,
        totalForwards: 0,
        totalMonsters: 0,
        totalSummons: 0,
        totalLimitBreak: 0,
        backups: {},
        forwards: {},
        monsters: {},
        summons: {},
        limitBreak: {},
        cards: {},
        totalCardsByCost: {},
        totalExBursts: 0,
        totalMultiplayable: 0,
        totalCrystalGenerators: 0,
        totalSpecials: 0,
        isLiked: false,
        totalJobs: {},
        totalCategories: {},
        deckPrice: 0,
        missingPrices: 0,
        missingOctgnIds: [],
        errata: {},
        bannedCards: [],
        restrictedCards: [],
        deckImageVariant: null
    };
}

export const deckCardsViewTypes = ["List", "Images", "Condensed Images"] as const;
export type DeckCardsViewType = typeof deckCardsViewTypes[number];